import { fetchData } from 'lib/fetchData';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from '_helpers';
import Wrapper from '../../../components/wrapper/Wrapper';

const Header = dynamic(import('components/header'));
const ImagesList = dynamic(import('components/imagesList'));

interface IProps {
  header: IHeaders;
  menu: any;
  footer: any;
  company: any;
  categories: any;
  gallery: any;
  page: any;
  count: any;
  seo: any;
  locale: any;
}

const Gallery: NextPage<IProps> = (props) => {
  const { asPath } = useRouter();
  const {
    menu,
    header,
    footer,
    company,
    gallery,
    categories,
    page,
    count,
    seo,
    locale,
  } = props;
  return (
    <Wrapper menu={menu} company={company} footer={footer}>
      <Head>
        <title>{seo.pages[4].title}</title>
        <meta name="description" content={seo.pages[4].description} />
        <link rel="icon" href={API + company.favicon.media.url} />
        <meta name="robots" content="index,follow" />
        <meta
          property="article:published_time"
          content={new Date().toLocaleTimeString()}
        />
        <meta
          name="publish_date"
          property="og:publish_date"
          content={new Date().toLocaleDateString()}
        />
        <meta
          name="author"
          property="article:author"
          content="Luccin Masirika"
        />
        {/* Open Graph */}
        <meta property="og:title" content={seo.pages[4].title} />
        <meta property="og:description" content={seo.pages[4].description} />
        <meta
          property="og:url"
          content={`https://uptodatedevelopers.com${asPath}`}
        />
        <meta property="og:image" content={API + seo.pages[4].media.url} />
        <meta property="og:site_name" content={company.name} />
        <meta property="og:locale" content={locale} />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={company.name} />
      </Head>
      <Header data={header.gallery} />
      <ImagesList
        path={`/${asPath.split('/')[3]}`}
        data={gallery}
        categories={categories}
        count={count}
        page={page}
      />
    </Wrapper>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const all = (await fetchData('/images')) || [];
  const limit = 12;
  let count = 0;

  interface IAll {
    media: IGallery[];
    gallery_category: ICategory;
    link: string;
  }

  let paths: any[] = [];

  all.forEach((x: IAll) => {
    let page = Math.ceil(x.media.length / limit);
    x.media.forEach((y: any, i) => {
      count++;
      if (
        paths.some(
          (item) =>
            item.params?.category === x.gallery_category.slug && page > 1
        )
      ) {
        paths.push({
          params: {
            page: `${page - 1}`,
            category: x.gallery_category.slug,
          },
        });
        page--;
        return;
      } else if (
        paths.some(
          (item) =>
            item.params?.category === x.gallery_category.slug && page === 1
        )
      ) {
        return;
      } else {
        paths.push({
          params: {
            page: page.toString(),
            category: x.gallery_category.slug,
          },
        });
      }
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const all = await fetchData(
    `/images?_gallery_category.slug=${params?.category}`
  );

  const limit = 12;
  let count = 0;
  let gallery: IGallery[] = [];

  interface IAll {
    media: IGallery[];
    gallery_category: ICategory;
    link: string;
  }

  all?.forEach((x: IAll) => {
    x?.media?.forEach((y: any) => {
      count++;
      gallery.push({
        _id: y?._id,
        link: x?.link,
        gallery_category: x?.gallery_category,
        media: y,
      });
    });
  });

  const pages = Math.ceil(count / limit);
  const page = params?.page ? +params.page - 1 : 0;
  const skip = page * limit;
  const start = limit * (1 + page);

  const menu = await fetchData(`/menu/?_locale=${locale}`);
  const company = await fetchData(`/company/?_locale=${locale}`);
  const footer = await fetchData(`/footer/?_locale=${locale}`);
  const header = await fetchData(`/headers/?_locale=${locale}`);
  const categories = await fetchData(`/gallery-categories/?_locale=${locale}`);
  const seo = await fetchData(`/seo/?_locale=${locale}`);

  if (all.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      gallery: gallery.slice(skip, +start),
      menu,
      company,
      footer,
      header,
      categories,
      count: pages,
      page: page + 1,
      seo,
      locale,
    },
    revalidate: 10,
  };
};

export default Gallery;
